import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../api';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@600,700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
.auth-root{min-height:100vh;display:flex;align-items:center;justify-content:center;
  background:linear-gradient(145deg,#06112b 0%,#112359 60%,#1a3570 100%);
  font-family:'DM Sans',sans-serif;padding:20px;position:relative;}
.auth-root::before{content:'';position:fixed;inset:0;z-index:0;
  background-image:linear-gradient(rgba(28,115,186,0.05) 1px,transparent 1px),
    linear-gradient(90deg,rgba(28,115,186,0.05) 1px,transparent 1px);
  background-size:52px 52px;pointer-events:none;}
.auth-card{position:relative;z-index:1;width:100%;max-width:420px;
  background:rgba(13,31,69,0.85);border:1px solid rgba(28,115,186,0.22);
  border-radius:20px;padding:40px 36px;box-shadow:0 24px 80px rgba(0,0,0,0.5);}
@media(max-width:480px){.auth-card{padding:28px 20px;}}
.auth-logo{text-align:center;margin-bottom:24px;}
.auth-logo a{font-family:'Clash Display',sans-serif;font-size:2rem;font-weight:700;
  color:#fff;text-decoration:none;letter-spacing:-1px;}
.auth-logo a span{color:#f5a623;}
.auth-icon{font-size:40px;text-align:center;margin-bottom:12px;}
.auth-title{font-family:'Clash Display',sans-serif;font-size:1.4rem;font-weight:700;
  color:#fff;margin-bottom:4px;text-align:center;}
.auth-sub{font-size:13px;color:#64748b;text-align:center;margin-bottom:24px;}
.auth-alert{padding:11px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;line-height:1.5;}
.auth-alert.error{background:rgba(248,113,113,0.1);border:1px solid rgba(248,113,113,0.25);color:#f87171;}
.auth-alert.success{background:rgba(52,211,153,0.1);border:1px solid rgba(52,211,153,0.25);color:#34d399;}
.auth-alert.warn{background:rgba(245,166,35,0.1);border:1px solid rgba(245,166,35,0.25);color:#fbbf24;}
.auth-field{margin-bottom:16px;}
.auth-label{display:block;font-size:11px;font-weight:700;color:#94a3b8;
  text-transform:uppercase;letter-spacing:1.5px;margin-bottom:6px;}
.auth-pw-wrap{position:relative;}
.auth-input{width:100%;padding:11px 14px;background:rgba(255,255,255,0.05);
  border:1px solid rgba(28,115,186,0.2);border-radius:10px;color:#e8f4fd;
  font-family:'DM Sans',sans-serif;font-size:14px;outline:none;transition:border 0.2s;}
.auth-input:focus{border-color:#2b8de0;}
.auth-input::placeholder{color:rgba(255,255,255,0.2);}
.auth-pw-wrap .auth-input{padding-right:44px;}
.auth-pw-eye{position:absolute;right:12px;top:50%;transform:translateY(-50%);
  background:transparent;border:none;color:#64748b;cursor:pointer;font-size:15px;padding:2px;}
.auth-strength{height:3px;border-radius:99px;background:rgba(255,255,255,0.07);margin-top:6px;overflow:hidden;}
.auth-strength-bar{height:100%;border-radius:99px;transition:width 0.3s,background 0.3s;}
.auth-btn{width:100%;padding:13px;border-radius:11px;border:none;
  background:linear-gradient(135deg,#1c73ba,#2b8de0);color:#fff;
  font-family:'DM Sans',sans-serif;font-size:15px;font-weight:700;
  cursor:pointer;transition:all 0.18s;margin-top:4px;box-shadow:0 4px 14px rgba(28,115,186,0.4);}
.auth-btn:hover:not(:disabled){transform:translateY(-1px);}
.auth-btn:disabled{opacity:0.6;cursor:not-allowed;}
.auth-back{display:block;text-align:center;margin-top:20px;font-size:13px;color:#5aaef7;text-decoration:none;}
`;

function pwStrength(pw) {
  if (!pw) return { pct: 0, color: 'transparent' };
  let s = 0;
  if (pw.length >= 8) s++; if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw)) s++; if (/[0-9]/.test(pw)) s++; if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 1) return { pct: 20, color: '#f87171' };
  if (s <= 2) return { pct: 40, color: '#fbbf24' };
  if (s <= 3) return { pct: 65, color: '#60a5fa' };
  return { pct: 100, color: '#34d399' };
}

export default function ResetPassword() {
  const [newPw,    setNewPw]    = useState('');
  const [confirmPw,setConfirmPw]= useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [token,    setToken]    = useState('');
  const [error,    setError]    = useState('');
  const [done,     setDone]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const strength = pwStrength(newPw);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    if (!t) setError('Invalid or missing reset link. Request a new one.');
    else setToken(t);
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword: newPw });
      setDone(true);
      setTimeout(() => navigate('/login?reset=done'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      <style>{CSS}</style>
      <div className="auth-card">
        <div className="auth-logo"><a href="/">flap<span>.</span></a></div>

        {done ? (
          <>
            <div className="auth-icon">✅</div>
            <h1 className="auth-title">Password reset!</h1>
            <p className="auth-sub">Redirecting you to login…</p>
          </>
        ) : (
          <>
            <div className="auth-icon">🔒</div>
            <h1 className="auth-title">Set new password</h1>
            <p className="auth-sub">Enter a strong new password for your account.</p>

            {error && <div className="auth-alert error">⚠️ {error}</div>}

            {!token ? (
              <div className="auth-alert warn">
                ⚠️ No reset token found.{' '}
                <Link to="/forgot-password" style={{color:'inherit',fontWeight:700}}>Request a new link →</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="auth-field">
                  <label className="auth-label">New Password</label>
                  <div className="auth-pw-wrap">
                    <input className="auth-input" type={showPw ? 'text' : 'password'}
                      value={newPw} onChange={e => setNewPw(e.target.value)}
                      required minLength={6} placeholder="At least 6 characters"
                      autoComplete="new-password"/>
                    <button type="button" className="auth-pw-eye"
                      onClick={() => setShowPw(p => !p)}>{showPw ? '🙈' : '👁'}</button>
                  </div>
                  {newPw && (
                    <div className="auth-strength">
                      <div className="auth-strength-bar" style={{width:`${strength.pct}%`,background:strength.color}}/>
                    </div>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-label">Confirm Password</label>
                  <input className="auth-input" type={showPw ? 'text' : 'password'}
                    value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                    required placeholder="Repeat new password"
                    autoComplete="new-password"
                    style={confirmPw && confirmPw !== newPw ? {borderColor:'#f87171'} : {}}/>
                  {confirmPw && confirmPw !== newPw && (
                    <div style={{fontSize:11,color:'#f87171',marginTop:4}}>Passwords do not match</div>
                  )}
                </div>

                <button type="submit" className="auth-btn"
                  disabled={loading || !newPw || !confirmPw || newPw !== confirmPw}>
                  {loading ? '⏳ Resetting…' : '🔒 Reset Password'}
                </button>
              </form>
            )}
            <Link to="/login" className="auth-back">← Back to login</Link>
          </>
        )}
      </div>
    </div>
  );
}